import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Lock, Eye, EyeOff, CheckCircle, AlertTriangle } from "lucide-react";
import {
  resetPasswordSchema,
  ResetPasswordFormData,
} from "@/lib/validators/auth";
import { useResetPassword } from "./hooks/useAuth";
import { LOGOPATH } from "@/lib/paths";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      token: token || "",
      email: email || "",
      password: "",
      passwordConfirmation: "",
    },
  });

  useEffect(() => {
    form.setValue("token", token || "");
    form.setValue("email", email || "");
  }, [token, email, form]);

  const { mutate: resetPassword, isPending } = useResetPassword();

  const onSubmit = async (data: ResetPasswordFormData) => {
    resetPassword(data, {
      onSuccess: () => {
        setResetSuccess(true);
      },
    });
  };

  // Invalid link state
  if (!token || !email) {
    return (
      <div className="min-h-screen bg-white flex">
        {/* App Name - Top Left */}
        <div className="absolute top-8 left-8 z-20">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
              <img
                src={LOGOPATH}
                alt="Logo"
                className="w-10 h-10 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">RoboBank</h1>
          </div>
        </div>

        {/* Left Column */}
        <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-red-50 via-red-100/50 to-white">
          <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 via-transparent to-red-400/5"></div>
          <div className="flex items-center justify-center w-full p-12">
            <div className="w-full max-w-lg h-96 bg-red-100/30 rounded-2xl flex items-center justify-center border border-red-200/50">
              <div className="text-center">
                <div className="w-24 h-24 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-12 h-12 text-white" />
                </div>
                <p className="text-red-600 font-medium">Invalid Reset Link</p>
                <p className="text-sm text-red-400 mt-2">
                  This link has expired or is invalid
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Error */}
        <div className="flex-1 lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md text-center">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-4">
              Invalid Reset Link
            </h2>
            <p className="text-slate-600 mb-8">
              This password reset link is invalid or has expired. Please request
              a new one.
            </p>
            <Button
              asChild
              className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
            >
              <Link to="/forgot-password">Request New Reset Link</Link>
            </Button>
            <div className="mt-6">
              <Link
                to="/login"
                className="text-sm text-indigo-600 hover:text-indigo-500 font-medium"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-white flex">
        {/* App Name - Top Left */}
        <div className="absolute top-8 left-8 z-20">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
              <img
                src={LOGOPATH}
                alt="Logo"
                className="w-10 h-10 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">RoboBank</h1>
          </div>
        </div>

        {/* Left Column - Success */}
        <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-indigo-50 via-indigo-100/50 to-white">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 via-transparent to-indigo-400/5"></div>
          <div className="flex items-center justify-center w-full p-12">
            <div className="w-full max-w-lg h-96 bg-indigo-100/30 rounded-2xl flex items-center justify-center border border-indigo-200/50">
              <div className="text-center">
                <div className="w-24 h-24 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-12 h-12 text-white" />
                </div>
                <p className="text-indigo-600 font-medium">
                  Password Reset Successful
                </p>
                <p className="text-sm text-indigo-400 mt-2">
                  Your account is now secure
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Success */}
        <div className="flex-1 lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md text-center">
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-4">
              Password Reset Successful
            </h2>
            <p className="text-slate-600 mb-8">
              Your password has been successfully reset. You can now login with
              your new password.
            </p>
            <Button
              asChild
              className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
            >
              <Link to="/login">Continue to Login</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Reset form state
  return (
    <div className="min-h-screen bg-white flex">
      {/* App Name - Top Left */}
      <div className="absolute top-8 left-8 z-20">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
            <img
              src={LOGOPATH}
              alt="Logo"
              className="w-10 h-10 object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">RoboBank</h1>
        </div>
      </div>

      {/* Left Column - Vector Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-indigo-50 via-indigo-100/50 to-white">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 via-transparent to-indigo-400/5"></div>
        <div className="flex items-center justify-center w-full p-12">
          <div className="w-full max-w-lg h-96 bg-indigo-100/30 rounded-2xl flex items-center justify-center border border-indigo-200/50">
            <div className="text-center">
              <div className="w-24 h-24 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-12 h-12 text-white" />
              </div>
              <p className="text-indigo-600 font-medium">
                Secure Password Reset
              </p>
              <p className="text-sm text-indigo-400 mt-2">
                Create a strong new password
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Reset Form */}
      <div className="flex-1 lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-2">
              Reset Your Password
            </h2>
            <p className="text-slate-600">
              Enter a new secure password for your account.
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-slate-700">
                      New Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter new password"
                          className="h-12 pr-12 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500 bg-white"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="passwordConfirmation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-slate-700">
                      Confirm New Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm new password"
                          className="h-12 pr-12 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500 bg-white"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={isPending}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {isPending ? "Resetting Password..." : "Reset Password"}
              </Button>
            </form>
          </Form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-indigo-600 hover:text-indigo-500 font-medium"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
